import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material";
import {
    Area, AreaChart, CartesianGrid, ResponsiveContainer,
    Tooltip, XAxis, YAxis,
} from "recharts";
import dayjs from "dayjs";

interface DatePoint {
    date: string;
    value: number;
}

interface Props {
    title: string;
    data?: DatePoint[];
    loading: boolean;
    color?: string;
    currencySymbol?: string;
    dateFormat?: string;
}

interface TooltipData { active?: boolean; payload?: { payload?: DatePoint; value?: number }[]; currencySymbol?: string; }
function CustomTooltip({ active, payload, currencySymbol }: TooltipData) {
    if (!active || !payload?.length) return null;
    const point = payload[0]?.payload as DatePoint;
    const value = payload[0].value;
    const formatted = currencySymbol
        ? `${currencySymbol} ${value?.toLocaleString("es-PY")}`
        : value?.toLocaleString("es-PY");
    return (
        <Box sx={{
            bgcolor: "grey.900", color: "common.white",
            px: 1.5, py: 1, borderRadius: "8px",
            fontSize: "0.8125rem", fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        }}>
            <Box sx={{ color: "grey.400", fontSize: "0.75rem", mb: 0.25 }}>
                {dayjs(point?.date).format("DD MMM YYYY")}
            </Box>
            <Box>{formatted}</Box>
        </Box>
    );
}

function EmptyState() {
    return (
        <Box sx={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" color="text.disabled">Sin datos para el período</Typography>
        </Box>
    );
}

export default function LineChartCard({ title, data, loading, color = "#635bff", currencySymbol, dateFormat = "DD/MM" }: Props) {
    const isEmpty = !loading && (!data || data.length === 0);
    const chartId = `gradient-${color.replace("#", "")}`;

    const formatted = data?.map((d) => ({
        ...d,
        dateLabel: dayjs(d.date).format(dateFormat),
    }));

    return (
        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ pb: "16px !important", flex: 1, display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1.5 }}>
                    {title}
                </Typography>

                {loading ? (
                    <Skeleton variant="rectangular" sx={{ borderRadius: "8px", flex: 1, minHeight: 220 }} />
                ) : isEmpty ? (
                    <EmptyState />
                ) : (
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={formatted} margin={{ left: 8, right: 8, top: currencySymbol ? 16 : 4, bottom: 4 }}>
                            <defs>
                                <linearGradient id={chartId} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={color} stopOpacity={0.18} />
                                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                horizontal
                                vertical={false}
                                stroke="var(--mui-palette-divider)"
                                strokeDasharray="0"
                            />
                            <XAxis
                                dataKey="dateLabel"
                                tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
                                axisLine={false}
                                tickLine={false}
                                interval="preserveStartEnd"
                            />
                            <YAxis
                                width={currencySymbol ? 80 : 35}
                                tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v: number) => currencySymbol ? `${currencySymbol} ${v.toLocaleString("es-PY")}` : v.toLocaleString("es-PY")}
                            />
                            <Tooltip content={<CustomTooltip currencySymbol={currencySymbol} />} cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: "3 3" }} />
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke={color}
                                strokeWidth={2}
                                fill={`url(#${chartId})`}
                                dot={false}
                                activeDot={{ r: 4, fill: color, strokeWidth: 0 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
}
